import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import Content from "./components/content.jsx";
import {subMonths} from "date-fns";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Filters from "./components/filters.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subMonths(new Date(), 1),
    dtEnd: new Date(),
    plate: '',
    lane: [],
    source: {value: "all", label: "Todos"}
});

const filterLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    plate: "Placa",
    lane: "Câmera",
    source: "Origem"
};

const initialTableColumns = {
    capture_time: {label: "D/H do evento", visible: true},
    plate: {label: "Placa", visible: true},
    description: {label: "Câmera", visible: true},
    source: {label: "Origem", visible: true},
    reasons: {label: "Naturezas", visible: true},
};

const AlarmList = () => {
    const [loading, setLoading] = useState(false);

    const [collapsed, setCollapsed] = useState(false);
    const buttonRef = useRef(null);
    const [fixedTop, setFixedTop] = useState(null);

    const [filters, setFilters] = useState(initialFilters);
    const [reportData, setReportData] = useState(null);
    const [tableColumns, setTableColumns] = useState(() => Object.fromEntries(
        Object.entries(initialTableColumns).map(([id, column]) => [id, {...column}])
    ));

    const [title] = useState("Lista de alarmes");
    const [headerSelected, setHeaderSelected] = useState(-1);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : ""
            }, filterLabels)
        );

    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd);

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                plate: filters.plate ?? '',
                lane: filters.lane?.map(i => i.value) ?? [],
                source: filters.source.value ?? 'all'
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd},
            filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/alarm_list/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;
            setReportData(data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {

            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);

        } finally {
            setLoading(false);
        }
    };

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        setFilters(initialFilters);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    return (
        <div className={"flex"}>

            <Template isLoading={loading}>
                <Template.Header>
                    <div className="flex justify-between w-full">
                        <Template.Title>{title}</Template.Title>
                        <HeaderButtons
                            exportable={true}
                            exportInSheets={true}
                            printable={true}
                            headerChange={true}
                            visibilityColumns={true}
                            columns={tableColumns}
                            setColumns={setTableColumns}
                            printComponentRef={printComponentRef}
                            reportData={reportData}
                            data={reportData?.length}
                            headerSelected={headerSelected}
                            setHeaderSelected={setHeaderSelected}
                        />
                    </div>
                </Template.Header>
                <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                    <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                        collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                        <Filters setFilters={setFilters} filters={filters}/>
                        <AutoDatetime setFilters={setFilters}/>

                    </Menu>
                </Template.Body>
            </Template>

            <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                <Card.Body>
                    <div className={`flex-1 ${!reportData || reportData?.length === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                        <NotAppliedContent reportData={reportData}/>
                        <NoDataContent reportData={reportData} data={reportData?.length}/>
                        <Content reportData={reportData} data={reportData?.length} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} tableColumns={tableColumns}/>
                    </div>
                </Card.Body>
            </Card>

        </div>
    );
};

export default AlarmList;